import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { UserType } from '../common/data/dummyRolesData';
import AuthContext from './authContext';
import { useOrganization } from './organizationContext';
import chatService from '../services/api/chatService';
import createChatRealtimeClient from '../services/realtime/chatRealtimeClient';

interface ChatUnreadContextValue {
	unreadCount: number;
	isLoading: boolean;
	refreshUnreadCount: () => Promise<number>;
}

const defaultValue: ChatUnreadContextValue = {
	unreadCount: 0,
	isLoading: false,
	refreshUnreadCount: async () => 0,
};

const ChatUnreadContext = createContext<ChatUnreadContextValue>(defaultValue);

/** @userstory US-CHAT-02 */
export const ChatUnreadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { isAuthenticated, isBootstrapped, userData } = useContext(AuthContext);
	const { selectedOrganization } = useOrganization();
	const [unreadCount, setUnreadCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const role = userData.role;
	const organizationId = selectedOrganization?.id;

	const refreshUnreadCount = useCallback(async () => {
		if (!isAuthenticated || !isBootstrapped) {
			setUnreadCount(0);
			return 0;
		}

		try {
			if (role === UserType.COMPANY) {
				if (!organizationId) {
					setUnreadCount(0);
					return 0;
				}

				const count = await chatService.getCompanyUnreadCount(organizationId);
				setUnreadCount(count);
				return count;
			}

			if (role === UserType.FREELANCE) {
				const count = await chatService.getTalentUnreadCount();
				setUnreadCount(count);
				return count;
			}
		} catch {
			setUnreadCount(0);
			return 0;
		}

		setUnreadCount(0);
		return 0;
	}, [isAuthenticated, isBootstrapped, organizationId, role]);

	useEffect(() => {
		if (!isAuthenticated || !isBootstrapped || !role) {
			setUnreadCount(0);
			return undefined;
		}

		if (role === UserType.COMPANY && !organizationId) {
			setUnreadCount(0);
			return undefined;
		}

		const realtimeClient = createChatRealtimeClient({
			onReconnected: () => {
				void refreshUnreadCount();
			},
		});

		const disposeUnreadCountUpdated = realtimeClient.onUnreadCountUpdated((payload) => {
			if (role === UserType.COMPANY && payload.viewerAuthorType !== 'Organization') {
				return;
			}

			if (role === UserType.FREELANCE && payload.viewerAuthorType !== 'Talent') {
				return;
			}

			setUnreadCount(payload.totalUnreadCount);
		});

		setIsLoading(true);

		void (async () => {
			try {
				await realtimeClient.connect();

				if (role === UserType.COMPANY && organizationId) {
					await realtimeClient.joinOrganization(organizationId);
				}

				if (role === UserType.FREELANCE) {
					await realtimeClient.joinTalent();
				}

				await refreshUnreadCount();
			} catch {
				setUnreadCount(0);
			} finally {
				setIsLoading(false);
			}
		})();

		return () => {
			disposeUnreadCountUpdated();
			void realtimeClient.disconnect();
		};
	}, [isAuthenticated, isBootstrapped, organizationId, refreshUnreadCount, role]);

	const value = useMemo(
		() => ({
			unreadCount,
			isLoading,
			refreshUnreadCount,
		}),
		[isLoading, refreshUnreadCount, unreadCount],
	);

	return <ChatUnreadContext.Provider value={value}>{children}</ChatUnreadContext.Provider>;
};

export const useChatUnread = (): ChatUnreadContextValue => useContext(ChatUnreadContext);

export default ChatUnreadContext;
